import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '../../../../../components/Button';
import { Caption } from '../../../../../components/Caption';
import { Card } from '../../../../../components/Card';
import { Checkbox } from '../../../../../components/Checkbox';
import { FormItem, FormLabel, FormLayout } from '../../../../../components/Form';
import { Modal } from '../../../../../components/Modal';
import { PhoneInput } from '../../../../../components/PhoneInput';
import { TextField } from '../../../../../components/TextField';
import { Wrapper } from '../../../../../components/Wrapper';
import { Routes } from '../../../../../constants/Routes';
import { useContext } from '../../../../../hooks/useContext';
import { useLocale } from '../../../../../hooks/useLocale';
import { useReservationId } from '../../../../../hooks/useReservationId';
import { useTransaction } from '../../../../../hooks/useTransaction';
import { SessionStore } from '../../../../../stores/SessionStore';
import { CardInputLayout } from '../../../AccountDetail/components/CardInputLayout';
import { Payjp3dSecureStore } from '../../../store/Payjp3dSecureStore';
import { errorStyle } from '../components/AccountsNameInput';
import { NameInput } from '../components/NameInput';
import { useActions } from './hooks/useActions';

export const AccountsNewNameInputFixWaitingListItem = observer(() => {
  const lang = useLocale();
  const session = useContext(SessionStore.Context);
  const { fixStatus, load, loadStatus, store } = useActions(session);
  const { encode } = useReservationId();

  const [authStateKnown, setAuthStateKnown] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [concent, setConcent] = useState(false);

  const [doCallback, status] = useTransaction(async (payjpId?: string) => {
    try {
      const params = new URLSearchParams(window.location.search);
      const url = params.get('url');
      const gclid = params.get('gclid');

      if (payjpId) {
        store.saveToSession();
        const finishParams = new URLSearchParams();
        finishParams.set('mode', 'waitingList');
        finishParams.set('waitingId', encode(store.id));
        if (url) finishParams.set('url', url);
        if (gclid) finishParams.set('gclid', gclid);

        const finishQuery = finishParams.toString();
        const finishUrl = `${window.location.origin}${Routes.Paths.Payjp3dSecureFinish}${
          finishQuery ? `?${finishQuery}` : ''
        }`;
        const backTo = encodeURIComponent(
          `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );

        const payjp3dSecureStore = new Payjp3dSecureStore(payjpId, finishUrl, backTo);
        await payjp3dSecureStore.redirect3dSecure();
        return;
      }
    } finally {
      setIsRegistering(false);
    }
  });

  const onSubmit = useRef<() => void>(doCallback);

  // 3Dセキュア認証で失敗した場合の再入力対応
  useEffect(() => {
    store.loadFromSession();
    store.clearSession();
  }, [store]);

  useEffect(() => {
    if (!session.authStateKnown) return;
    load();
    setAuthStateKnown(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.authStateKnown]);

  if (!authStateKnown || loadStatus.running || fixStatus.running) {
    return (
      <Modal visible>
        <Card>ユーザー情報を取得しています...</Card>
      </Modal>
    );
  }
  const isLegacy = new URLSearchParams(window.location.search).get('type') === 'LEGACY';

  return (
    <Wrapper>
      <Modal visible={isRegistering}>
        <Card>患者プロフィール情報を登録しています...</Card>
      </Modal>
      <Caption type="header" underline align="center">
        {lang === 'en' ? 'Profile registration' : '患者プロフィール登録'}
      </Caption>
      <FormLayout>
        <FormItem>
          <NameInput
            value={store.name || { sei: '', mei: '' }}
            onChange={v => store.setName(v)}
            errors={store.errors}
            lang={lang}
          />
        </FormItem>
        <FormItem>
          <FormLabel required lang={lang}>
            {lang === 'en' ? 'Phone number' : '電話番号'}
          </FormLabel>
          <PhoneInput
            color="white"
            value={store.phone}
            onChange={v => store.setPhone(v)}
            error={store.errors.some(e => e.field === 'phone')}
            errors={store.errors}
          />
        </FormItem>
        <FormItem>
          <CardInputLayout
            required
            phone={store.phone}
            callback={async t => {
              setIsRegistering(true);
              store.setCard({
                brand: t.brand,
                id: t.payjpID,
                expAt: t.expAt,
                fourDigit: t.fourDigit,
              });
              return doCallback(t.payjpID);
            }}
            onSubmit={onSubmit}
            onStatusChange={s => store.cardStatus.setCardTransactionStatus(s)}
            onPreCardValidateChange={v => store.cardStatus.setPreCardValidate(v)}
          />
        </FormItem>
        {!isLegacy && (
          <FormItem>
            <FormLabel>メールアドレス(任意)</FormLabel>
            <TextField type="email" color="white" value={store.mail} onChange={v => store.setMail(v)} />
            <Caption type="small">予約時にGoogleカレンダー招待をお送りします。</Caption>
          </FormItem>
        )}
        <FormItem>
          <Checkbox
            label={lang === 'en' ? 'I agree with the Terms & Conditions ' : 'TENの利用規約に同意する'}
            checked={concent}
            onChange={v => setConcent(v.target.checked)}
          />
          <div>
            <a style={{ textDecoration: 'underline' }} href="/terms" target="_blank">
              {lang === 'en' ? 'Confirm Terms of Service' : '利用規約を確認(別タブが開きます)'}
            </a>
          </div>
        </FormItem>
        <div className={errorStyle}>{store.cardStatus.cardTransactionStatus?.error}</div>
        <Button
          type="submit"
          block
          onClick={() => {
            setIsRegistering(true);
            onSubmit.current();
          }}
          disabled={
            status.running ||
            !store.valid ||
            !concent ||
            store.cardStatus.cardTransactionStatus?.running ||
            store.cardStatus.hasPreCardError ||
            !store.cardStatus.preCardInputCompleted
          }
        >
          {lang === 'en' ? 'Confirm registration' : '登録してキャンセル待ちを確定'}
        </Button>
      </FormLayout>
    </Wrapper>
  );
});
